import React from 'react';
import { ArrowLeft, Calendar, MapPin, Scale, Gavel } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';

const EuroCommerceTaxArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Industry Highlights
          </Button>
          
          <div className="flex items-center gap-4 mb-4">
            <Badge className="bg-purple-500">
              <Scale className="h-3 w-3 mr-1" />
              {t('article.eurocommerce.badge')}
            </Badge>
            <div className="flex items-center text-gray-600">
              <Calendar className="h-4 w-4 mr-2" />
              {t('article.eurocommerce.date')}
            </div>
            <div className="flex items-center text-gray-600">
              <MapPin className="h-4 w-4 mr-2" />
              {t('article.eurocommerce.location')}
            </div>
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('article.eurocommerce.title')}
          </h1>
          
          <p className="text-xl text-gray-600 leading-relaxed">
            {t('article.eurocommerce.summary')}
          </p>
        </div>

        {/* Featured Image */}
        <div className="mb-8">
          <img 
            src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
            alt={t('article.eurocommerce.imageCaption')}
            className="w-full h-96 object-cover rounded-lg shadow-lg"
          />
          <p className="text-sm text-gray-500 mt-2 text-center">
            {t('article.eurocommerce.imageCaption')}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Highlight Box */}
            <Card className="border-l-4 border-l-purple-500 bg-purple-50">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <Gavel className="h-6 w-6 text-purple-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-purple-800 mb-2">Key Highlight</h3>
                    <p className="text-purple-700">{t('article.eurocommerce.highlight')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Article Sections */}
            <div className="prose prose-lg max-w-none">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.eurocommerce.section1.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.eurocommerce.section1.content')}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.eurocommerce.section2.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.eurocommerce.section2.content')}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.eurocommerce.section3.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.eurocommerce.section3.content')}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.eurocommerce.section4.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.eurocommerce.section4.content')}
                </p>
              </div>
            </div>

            {/* Impact Analysis */}
            <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
              <CardHeader>
                <CardTitle className="flex items-center text-purple-800">
                  <Scale className="h-5 w-5 mr-2" />
                  {t('article.eurocommerce.impact.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-purple-700">
                  {t('article.eurocommerce.impact.content')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Key Metrics */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Key Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Organization</span>
                  <span className="font-semibold">EuroCommerce</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Proposed Fee</span>
                  <span className="font-semibold text-purple-600">€2.00</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">EU Warehouses</span>
                  <span className="font-semibold text-purple-600">€0.50</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Status</span>
                  <Badge className="bg-purple-500">Policy Reform</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Source */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Source</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  EcommerceNews.eu
                </p>
                <Button 
                  className="w-full" 
                  onClick={() => window.open('https://ecommercenews.eu/eurocommerce-parcel-tax-should-be-eu-wide/', '_blank')}
                >
                  Read Original Article
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EuroCommerceTaxArticle;